import { useState } from 'react';
import { AnswerControllerProps } from './AnswerController';
import { useToast } from '@/hooks/V3/useToast';
import { usePost, CPLAT_API_ROUTES } from '@/api';
import { useAppSelector } from '@/redux/store';
import { Button } from '@/components/atoms/NewPcButton';
import { useMembershipInquiryPreviewTutorial } from '@/components/membership/MembershipInquiryPreviewTutorial';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/atoms/Dialog';

type PickedAnswerControllerProps = Pick<AnswerControllerProps, 'inquiry' | 'setComment'>;

const MAX_RECOMMENDATIONS = 3;

const GenerateAnswer = ({ inquiry, setComment }: PickedAnswerControllerProps) => {
  const [open, setOpen] = useState(false);
  const [aiRecommendations, setAiRecommendations] = useState<string[]>([]);
  const [isAILoading, setIsAILoading] = useState(false);

  const { mutate: generateAIComment } = usePost(CPLAT_API_ROUTES.generateAIRecommendedAnswer);

  const user = useAppSelector((state) => state.user);
  const { showToast } = useToast();
  const { openTutorial } = useMembershipInquiryPreviewTutorial();

  // 버튼 클릭 핸들러
  const handleAIGenerateClick = () => {
    if (openTutorial('generateAnswer')) return;

    setOpen(true);
    if (aiRecommendations.length < MAX_RECOMMENDATIONS) {
      handleAIGenerate();
    }
    // 3개 이상이면 다이얼로그만 열림 (추가 요청 없음)
  };

  // 실제 생성 로직
  const handleAIGenerate = () => {
    if (isAILoading || aiRecommendations.length >= MAX_RECOMMENDATIONS) return;
    setIsAILoading(true);
    generateAIComment(
      {
        CplatToken: user.CplatToken,
        productName: inquiry.ProductName,
        inquiryContent: inquiry.Content,
        inquiryType: inquiry.InquiryType,
      },
      {
        onSuccess: (response) => {
          const answer = response.data?.data?.[0]?.AiRecommendedAnswer;
          if (answer) {
            setAiRecommendations((prev) => {
              if (prev.length >= MAX_RECOMMENDATIONS) return prev;
              return [...prev, answer];
            });
          }
          setIsAILoading(false);
        },
        onError: () => {
          showToast('warning', 'AI 답변 생성에 실패했습니다.');
          setIsAILoading(false);
          setOpen(false);
        },
      },
    );
  };

  const handleSelectRecommendation = (text: string) => {
    setComment?.(text);
    setOpen(false);
    setAiRecommendations([]); // 선택 후 추천 리스트 초기화 (필요시)
  };

  const isMaxReached = aiRecommendations.length >= MAX_RECOMMENDATIONS;

  return (
    <>
      <Button
        onClick={handleAIGenerateClick}
        variant="secondary"
        size="xsmall"
        disabled={isAILoading}
        isLoading={isAILoading}
      >
        {aiRecommendations.length === 0 ? 'AI로 답변 생성' : 'AI 답변 재생성'}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent noCloseBtn>
          <DialogHeader>
            <DialogTitle>AI 답변 추천</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {aiRecommendations.map((text, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleSelectRecommendation(text)}
                className="w-full cursor-pointer rounded border p-4 text-left hover:bg-gr-50"
              >
                {text}
              </button>
            ))}
            {isAILoading && (
              <div className="flex h-20 items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
              </div>
            )}
            <Button
              variant="secondary"
              className="w-full"
              isLoading={isAILoading}
              onClick={handleAIGenerate}
              disabled={isAILoading || isMaxReached}
            >
              {isMaxReached ? '최대 3회까지 생성' : 'AI 답변 재생성'}
            </Button>
            {isMaxReached && (
              <div className="text-center text-xs text-gr-400">
                AI 답변 추천은 최대 3회까지 가능합니다.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GenerateAnswer;
